import type React from 'react';
import { useEffect, useState } from 'react';

import { Control, DropdownMenu } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { Controller, useFormContext } from 'react-hook-form';

import { PermissionTag } from '@vlabs/monokit/entities/permission-tags';
import type { SourceGroupOption } from '@vlabs/monokit/entities/sources';
import {
  GROUP_NAME_PATTERN,
  loadDepartmentOptions,
  loadPermissionTagOptions,
} from '@vlabs/monokit/entities/sources';
import { CopyableText } from '@vlabs/monokit/shared/ui/copyable-text';
import { FormFieldGroup } from '@vlabs/monokit/shared/ui/form-field-group';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useSourceGroupGeneralInfoTranslation } from './i18n';
import st from './SourceGroupGeneralInfo.module.sass';
import type { GroupSettingsFormValues } from './types';

interface SourceGroupGeneralInfoProps {
  groupId: string;
}

function SourceGroupGeneralInfo({ groupId }: SourceGroupGeneralInfoProps): React.JSX.Element {
  const { t } = useSourceGroupGeneralInfoTranslation();
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<GroupSettingsFormValues>();

  const [departmentOptions, setDepartmentOptions] = useState<SourceGroupOption[]>([]);
  const [permissionTagOptions, setPermissionTagOptions] = useState<SourceGroupOption[]>([]);

  useEffect(() => {
    loadDepartmentOptions().then(setDepartmentOptions);
    loadPermissionTagOptions().then(setPermissionTagOptions);
  }, []);

  const getSelectedOption = (
    value: string,
    options: SourceGroupOption[],
  ): SourceGroupOption | undefined => (
    options.find((option) => option.value === value)
    ?? (value ? { label: value, value } : undefined)
  );

  const permissionTag = watch('permission_tag');

  let permissionTagTrigger: React.JSX.Element;
  if (permissionTag) {
    // div-обёртка принимает ref/обработчики от MenuTrigger (PermissionTag не forwardRef)
    permissionTagTrigger = (
      <div className={st.PermissionTagTrigger}>
        <PermissionTag permissionTag={permissionTag} />
      </div>
    );
  } else {
    permissionTagTrigger = (
      <Control.Button
        kind="secondary"
        size="small"
        variant="outlined"
      >
        {t('fields.choose_permission_tag')}
      </Control.Button>
    );
  }

  return (
    <div className={st.Root}>
      <h2 className={st.Title}>{t('title')}</h2>

      <FormFieldGroup>
        <FormFieldGroup.Item label={t('fields.group_name')}>
          <Control.Input
            errorMessage={errors.group_name?.message}
            errors={errors}
            id="group_name"
            {...register('group_name', {
              pattern: { message: t('validation.name_pattern'), value: GROUP_NAME_PATTERN },
              required: t('validation.required'),
            })}
          />
        </FormFieldGroup.Item>

        <FormFieldGroup.Item label={t('fields.description')}>
          <Control.Textarea
            className={st.DescriptionTextarea}
            errors={errors}
            id="description"
            {...register('description')}
          />
        </FormFieldGroup.Item>

        <FormFieldGroup.Item label={t('fields.owner_department')}>
          <Controller
            control={control}
            name="owner_department"
            render={({ field: { value, onChange } }) => (
              <Select
                classNamePrefix="SourceGroupOwnerDepartment"
                errorMessage={errors.owner_department?.message}
                errors={errors}
                menuPortalTarget={document.body}
                onChange={(option) => onChange(option?.value ?? '')}
                options={departmentOptions}
                placeholder={t('fields.owner_department')}
                styles={selectMenuPortalStyles}
                value={getSelectedOption(value, departmentOptions)}
              />
            )}
            rules={{ required: t('validation.required') }}
          />
        </FormFieldGroup.Item>

        <FormFieldGroup.Item label={t('fields.permission_tag')}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger
              render={permissionTagTrigger}
            />
            <DropdownMenu.Portal>
              <DropdownMenu.Positioner style={{ zIndex: 60 }}>
                <DropdownMenu.Popup>
                  {permissionTagOptions.map((option) => (
                    <DropdownMenu.Item
                      key={option.value}
                      onClick={() => setValue('permission_tag', option.value, { shouldDirty: true })}
                    >
                      {option.label}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Popup>
              </DropdownMenu.Positioner>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </FormFieldGroup.Item>

        <FormFieldGroup.Item label={t('fields.group_id')}>
          <CopyableText value={groupId} />
        </FormFieldGroup.Item>
      </FormFieldGroup>
    </div>
  );
}

export { SourceGroupGeneralInfo };
